



















































import {Vue, Component, Prop, Watch} from 'vue-property-decorator'
import {State} from 'vuex-class'

import {VFlex, VForm, VIcon, VLayout} from 'vuetify/lib'
import {ValidationProvider} from 'vee-validate'
import Utils from '@/utils'
import moment from 'moment'
import {AppConfigState} from '@/store/modules/configuration'

@Component({
  components: {
    InputDatePicker: Utils.loadComponent('proxy/Inputs/InputDatePicker'),
    InputTimePicker: Utils.loadComponent('proxy/Inputs/InputTimePicker'),
    VForm,
    VLayout,
    VFlex,
    VIcon,
    ValidationProvider,
  },
  name: 'DateTimeRangeField',
})
export default class DateTimeRangeField extends Vue {
  @State('configuration') public configState!: AppConfigState
  @State((state) => state.profile.language) public lang!: string

  @Prop({
    type: [String, Object],
    default: '',
  }) public readonly rules!: string | object

  @Prop({
    type: Object,
    default: null,
  }) public readonly value!: { start: number , end: number} | null

  public innerValue: string | null = null

  public today: string = moment().format('YYYY-MM-DD')

  @Watch('innerValue')
  public onInnerValueChange(val: string | null) {
    if (val) {
      const split: string[] = val.split('|').sort()
      this.$emit('input', {start: split[0], end: split[1]})
    } else {
      this.$emit('input', val)
    }
  }

  @Watch('value')
  public onValueChange(val: { start: number , end: number} | null) {
    this.innerValue = val ? val.start + '|' + val.end : val
  }

  public get tsd() {
    return this.configState.appConfig?.time_slot_dimension || 30
  }

  public get min() {
    const tsd = this.tsd
    const u = this.unpack()

    const closestSlot = (to?: moment.Moment) => {
      const n = to ? to : moment()
      const m = n.minutes()
      const rest = m % tsd
      if (m < tsd) {
        return n.minutes(0)
      } else {
        return n.subtract(rest, 'minutes')
      }
    }

    const checkinTime = () => {
      if ((u?.start.date || this.today) !== this.today) {
        return moment().startOf('day')
      } else {
        return closestSlot()
      }
    }

    const checkoutDate = () => {
      const lastSlot = moment('24:00', 'HH:mm').subtract(tsd, 'minutes')
      const m = u?.start.date ? moment(u.start.date, 'YYYY-MM-DD') : checkinTime()
      if (u?.start.time === lastSlot.format('LT')) {
        return m.add(1, 'day')
      }
      return m
    }

    const checkoutTime = () => {
      const t = u?.start.time ? moment(u.start.time, 'LT') : moment()
      if (!!u && u?.start.date !== u?.end.date) {
        return t.startOf('day')
      } else {
        return closestSlot(t).add(tsd, 'minutes')
      }
    }

    return {
      start: {
        date: this.today,
        time: checkinTime().format('LT'),
      },
      end: {
        date: checkoutDate().format('YYYY-MM-DD'),
        time: checkoutTime().format('LT'),
      },
    }
  }

  public get timeSlotsCheckIn(): string[] {
    const tsd = this.tsd
    const min = this.min?.start.time
    const arr = []
    const now = moment(min, 'LT')
    const end = moment().endOf('day')
    const t = moment(min, 'LT').startOf('hour')

    while (t.isBefore(end)) {
      if (moment.duration(now.diff(t)).asMinutes() >= tsd) {
        t.add(tsd, 'm')
        continue
      }
      arr.push(t.format('LT'))
      t.add(tsd, 'm')
    }
    return arr
  }

  public get timeSlotsCheckOut(): string[] {
    const tsd = this.tsd
    const min = this.min?.end.time
    const arr = []
    const now = moment(min, 'LT')
    const t = moment(min, 'LT')
    const end = moment().endOf('day')

    while (t.isBefore(end)) {
      if (moment.duration(now.diff(t)).asMinutes() > tsd) {
        t.add(tsd, 'm')
        continue
      }
      arr.push(t.format('LT'))
      t.add(tsd, 'm')
    }
    return arr
  }

  public get innerValueUnpacked() {
    if (!this.innerValue) {
      return null
    }
    return this.unpack(this.innerValue) || null
  }

  protected unpack(val = this.innerValue) {
    if (val) {
      const t = val
        .split('|')
        .map((t0) => Number(t0))
        .map((t1) => {
          const m = moment.unix(t1)
          return {date: m.format('YYYY-MM-DD'), time: m.format('LT')}
        })
      return {start: t[0], end: t[1]}
    }
  }

  protected pack(x: 'start' | 'end', obj: { date: string, time: string }) {
    const inner = this.innerValue || moment().format('X|X')
    const timestamp = moment(obj.date + ' ' + obj.time, 'YYYY-MM-DD LT').unix()
    const arr = inner
      .split('|')
      .map((s) => Number(s))

    if (x === 'start') {
      arr[0] = timestamp
    } else {
      arr[1] = timestamp
    }

    this.innerValue = arr.join('|')
    this.checkValidPack(x, obj, arr, timestamp)
  }

  protected checkValidPack(x: 'start' | 'end', obj: { date: string; time: string }, arr: number[], t: number) {
    const tsd = this.tsd
    const m = moment(obj.date + ' ' + obj.time, 'YYYY-MM-DD LT')
    if ((arr[1] <= t) && (x === 'start')) {
      if (x === 'start') {
        m.add(tsd, 'minutes')
        this.pack('end', {date: m.format('YYYY-MM-DD'), time: m.format('LT')})
      } else if ((arr[0] >= t) && (x === 'end')) {
        m.subtract(tsd, 'minutes')
        this.pack('start', {date: m.format('YYYY-MM-DD'), time: m.format('LT')})
      }
    }
  }

  protected setCheckInDate(date: string) {
    const unpacked = this.unpack()
    if (unpacked?.start.date) {
      unpacked.start.date = date
      this.pack('start', unpacked.start)
    }
    if (this.min.start.time) {
      this.setCheckInTime(this.min.start.time)
    }
  }

  protected setCheckInTime(time: string) {
    const unpacked = this.unpack()
    if (unpacked?.start.time) {
      unpacked.start.time = time
      this.pack('start', unpacked.start)
    }
  }

  protected setCheckOutDate(date: string) {
    const unpacked = this.unpack()
    if (unpacked?.end.date) {
      unpacked.end.date = date
      this.pack('end', unpacked.end)
    }
  }

  protected setCheckOutTime(time: string) {
    const unpacked = this.unpack()
    if (unpacked?.end.time) {
      unpacked.end.time = time
      this.pack('end', unpacked.end)
    }
  }

  protected allowedStep(m: number): boolean {
    return m % this.tsd === 0
  }

  protected created() {
    const val = this.value
    if (val) {
      const start = val.start
      const minStart = moment(this.min.start.date + ' ' + this.min.start.time, 'YYYY-MM-DD LT').unix()
      if (minStart > start) {
        this.pack('start', this.min.start)
      } else {
        this.innerValue = val ? `${val.start}|${val.end}` : val
      }
    } else if (!this.innerValue) {
      const min = this.min
      const start = min.start
      const end = min.end
      this.pack('start', start)
      this.pack('end', end)
    }
  }
}
